import { canvasToBlob, getExportBlob } from './svgExport';
import { EXPORT_MARGIN_PX } from './consts';
import { getCanvasWithBackgroundColor } from './canvasUtil';
import {
  directExportScale,
  renderGraphComponentToCanvas,
} from './renderGraphComponentToCanvas';
import { renderGraphComponentToSvg } from './renderGraphComponentToSvg';
import {
  deliverExport,
  exportInFlight,
  isExportCapturing,
  reportExportFailure,
  trackViewExport,
} from './exportSink';
import { GraphComponent, Insets, SvgExport, Rect } from '@ardoq/yfiles';
import { htmlToCanvas } from './htmlToCanvas';
import { isSafari } from '@ardoq/common-helpers';
import { logError } from '@ardoq/logging';
import { ViewIds } from '@ardoq/api-types';
import { Resolvable } from './Resolvable';
import { noop } from 'lodash';
import {
  EXPORT_FORMAT,
  ExportLabels,
  ExportedViewMetadata,
  GetStyleSheetsFn,
} from './types';
import { alert, ConfirmResult } from '@ardoq/modal';
import {
  CANVAS_MAXIMUM_AREA,
  CANVAS_MAXIMUM_DIMENSION,
  PARTIALLY_RENDERED_VIEWS,
  VIEW_EXPORT_HEADER_SELECTOR,
  VIEW_LEGEND_SELECTOR,
} from './consts';
import { viewExportCommands } from './commands';
import {
  composeExportMetadata,
  confirmLargeFile,
  getExportProfilingTransaction,
} from './exportUtil';
import * as profiling from '@ardoq/profiling';
import { dispatchAction, ofType, action$, withNamespace } from '@ardoq/rxbeach';
import { viewExportRequested, viewReadyForExport } from './actions';
import { take, timeout } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

const isFunction = <T>(resolvable: Resolvable<T>): resolvable is () => T =>
  typeof resolvable === 'function';

const resolve = <T>(resolvable: Resolvable<T>) =>
  isFunction(resolvable) ? resolvable() : resolvable;

const findBesideContainer = (
  container: Element | null | undefined,
  selector: string
): HTMLElement | undefined =>
  container?.parentElement?.querySelector<HTMLElement>(selector) ?? undefined;

type SaveFailure = {
  error: unknown;
  format: EXPORT_FORMAT;
  showExportFailureModal: ((format: EXPORT_FORMAT) => void) | undefined;
};

const handleSaveFailure = ({
  error,
  format,
  showExportFailureModal,
}: SaveFailure): void => {
  const capturing = isExportCapturing();
  reportExportFailure(error);
  if (!capturing) {
    showExportFailureModal?.(format);
  }
  logError(error as Error, 'Failed to save export');
};

export enum TrackingExportFormat {
  PNG = 'png',
  PDF = 'pdf (print dialog)',
  PRESENTATION = 'add to presentation',
  PLANTUML = 'plantuml',
  PPTX = 'pptx',
}

export type ExportViewEvent = {
  name: ViewIds;
  format: TrackingExportFormat | EXPORT_FORMAT;
  isViewpointMode: boolean;
};

type ExportBaseParams<T extends Element = Element> = {
  pngConfirmExport?: () => Promise<boolean | ConfirmResult>;
  trackingFunction?: (metadata: ExportViewEvent) => void;
  addToPresentation?: (name: ViewIds, isViewpointMode: boolean) => void;
  getStyleSheets: GetStyleSheetsFn;
  showExportFailureModal?: (format: EXPORT_FORMAT) => void;
  exportToLucidchart?: () => void;
  container: Resolvable<T | null>;
  showPresentationOption?: boolean;
  labels?: ExportLabels;
};

export type ExportHandlerArgs<T extends Element = Element> =
  ExportBaseParams<T> & {
    exportedViewMetadata: ExportedViewMetadata;
  };
export type ExportHandler<T extends Element = Element> = (
  args: ExportHandlerArgs<T>
) => {
  exportToPng: () => Promise<void>;
  exportToSvg?: () => Promise<void>;
  addToPresentation?: () => void;
};
type WithGraphComponent = { graphComponent: Resolvable<GraphComponent | null> };
type WithExportFormat = { format: EXPORT_FORMAT; fileName?: string };
type WithSVGClone = { svgClone?: SVGElement | null };
type WithExportProfilingTransaction = { transaction: profiling.Transaction };
/** render canvas visuals to true vector SVG instead of embedding rasters (see {@link renderGraphComponentToSvg}). */
type WithCanvasToSvg = { canvasToSvg?: boolean };

/**
 * determines the scale at which to export the graph.
 *
 * the scale will be increased if the devicePixelRatio is > 1, to ensure clear font rendering.
 *
 * the scale will be limited to ensure the exported image is no larger than what can be drawn via {@link CanvasRenderingContext2D.drawImage} or exported via {@link HTMLCanvasElement.toBlob}.
 */
const exportScale = (contentBounds: Rect, devicePixelRatio: number): number => {
  const { width, height } = contentBounds;
  const area = devicePixelRatio * (width * height);
  const maxDimension = devicePixelRatio * Math.max(width, height);

  const dimensionLimitedScale =
    maxDimension > CANVAS_MAXIMUM_DIMENSION
      ? CANVAS_MAXIMUM_DIMENSION / maxDimension
      : devicePixelRatio;
  const areaLimitedScale =
    area > CANVAS_MAXIMUM_AREA
      ? Math.sqrt(CANVAS_MAXIMUM_AREA / area)
      : devicePixelRatio;

  return Math.min(devicePixelRatio, dimensionLimitedScale, areaLimitedScale);
};
const prepareSVGExport = ({ graphComponent }: WithGraphComponent) => {
  const graphComponentResolved = resolve(graphComponent);
  if (!graphComponentResolved) return null;
  graphComponentResolved.updateContentBounds();
  const { contentBounds, devicePixelRatio } = graphComponentResolved;
  const scale = exportScale(contentBounds, devicePixelRatio);
  const exporter = new SvgExport(contentBounds, scale);
  exporter.margins = new Insets(25);
  exporter.encodeImagesBase64 = true;
  exporter.inlineSvgImages = true;

  const result = exporter.exportSvg(graphComponentResolved) as SVGElement;
  const defs = result.querySelector(':scope > defs');
  if (defs) {
    // Apr 2024: due to a bug in Chrome, placing the defs after the content of the svg, as yFiles does, will result in marker endings not rendering. feels like a race condition, even though we are just reading from the local disk.
    // this works around the problem by moving the defs up above the svg document content.
    result.insertBefore(defs, result.firstChild);
  }
  return result;
};

/**
 * exports a fully canvas-rendered yFiles view to PNG by rendering it directly to a 2D canvas, bypassing {@link SvgExport}.
 *
 * this is the only reliable path for large all-canvas diagrams (e.g. the modernized block diagram): the SVG export embeds every canvas visual as a base64 raster, and the browser fails to decode the resulting SVG at large sizes, producing a corrupt image.
 *
 * @returns true if the export was handled here, false if the view is not fully canvas-rendered and the caller should fall back to the SVG export path.
 */
const exportGraphComponentToPng = async ({
  container,
  graphComponent,
  exportedViewMetadata,
  fileName,
  trackingFunction,
  getStyleSheets,
  showExportFailureModal,
  transaction,
}: ExportHandlerArgs &
  WithGraphComponent &
  WithExportFormat &
  WithExportProfilingTransaction): Promise<boolean> => {
  const resolvedGraphComponent = resolve(graphComponent);
  const resolvedContainer = resolve(container);
  if (!resolvedGraphComponent || !resolvedContainer) return false;

  resolvedGraphComponent.updateContentBounds();
  const { contentBounds, devicePixelRatio } = resolvedGraphComponent;
  const scale = directExportScale(
    contentBounds,
    devicePixelRatio,
    EXPORT_MARGIN_PX
  );

  const graphCanvas = await renderGraphComponentToCanvas(
    resolvedGraphComponent,
    scale
  ).catch(error => {
    logError(
      error as Error,
      'Direct canvas export failed, falling back to SVG'
    );
    return null;
  });
  if (!graphCanvas) return false;

  const { name, isViewpointMode } = exportedViewMetadata;
  const mutableExportMetaData = composeExportMetadata(
    exportedViewMetadata,
    EXPORT_FORMAT.PNG
  );
  const legendElement = findBesideContainer(
    resolvedContainer,
    VIEW_LEGEND_SELECTOR
  );
  const headerElement = findBesideContainer(
    resolvedContainer,
    VIEW_EXPORT_HEADER_SELECTOR
  );

  viewExportCommands.notifyExportStarted(name);
  trackViewExport(trackingFunction, {
    name,
    format: EXPORT_FORMAT.PNG,
    isViewpointMode,
  });

  try {
    const canvasWithBackground = await getCanvasWithBackgroundColor({
      profilingTransaction: transaction,
      mutableTrackingData: mutableExportMetaData,
      inputCanvas: graphCanvas,
      color: '#FFFFFF',
      getStyleSheets,
      viewLegend: legendElement,
      viewHeader: headerElement,
    });
    const blob = await canvasToBlob(canvasWithBackground);
    const fileSaverSpan = profiling.startSpan(
      transaction,
      'waiting for file saver'
    );
    deliverExport(blob, `${fileName || name}.${EXPORT_FORMAT.PNG}`);
    profiling.endSpan(fileSaverSpan);
    viewExportCommands.notifyExportSucceeded(name);
  } catch (error) {
    handleSaveFailure({
      error,
      format: EXPORT_FORMAT.PNG,
      showExportFailureModal,
    });
  }

  profiling.endTransaction(transaction, {
    viewId: name,
    metadata: mutableExportMetaData,
  });
  viewExportCommands.notifyExportFinished(name);
  return true;
};

/**
 * Exports a fully canvas-rendered yFiles view to a true vector SVG
 * by rendering its canvas visuals through {@link renderGraphComponentToSvg},
 * rather than embedding them as rasters (as the default `SvgExport` path does).
 *
 * @returns true if handled here, false if the view is not fully canvas-rendered
 * and the caller should fall back to the default SVG export path.
 */
const exportGraphComponentToSvg = async ({
  container,
  graphComponent,
  exportedViewMetadata,
  fileName,
  trackingFunction,
  getStyleSheets,
  showExportFailureModal,
  transaction,
}: ExportHandlerArgs &
  WithGraphComponent &
  WithExportFormat &
  WithExportProfilingTransaction): Promise<boolean> => {
  const resolvedGraphComponent = resolve(graphComponent);
  if (!resolvedGraphComponent) return false;

  const { name, isViewpointMode } = exportedViewMetadata;
  const mutableExportMetaData = composeExportMetadata(
    exportedViewMetadata,
    EXPORT_FORMAT.SVG
  );
  const resolvedContainer = resolve(container);
  const legendElement = findBesideContainer(
    resolvedContainer,
    VIEW_LEGEND_SELECTOR
  );

  resolvedGraphComponent.updateContentBounds();
  const svg = await renderGraphComponentToSvg(resolvedGraphComponent, 1, {
    legendElement,
    title: name,
    getStyleSheets,
    transaction,
    mutableTrackingData: mutableExportMetaData,
  }).catch(error => {
    logError(
      error as Error,
      'SVG export failed, falling back to default SVG export'
    );
    return null;
  });
  if (!svg) return false;

  viewExportCommands.notifyExportStarted(name);
  trackViewExport(trackingFunction, {
    name,
    format: EXPORT_FORMAT.SVG,
    isViewpointMode,
  });
  try {
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    deliverExport(blob, `${fileName || name}.${EXPORT_FORMAT.SVG}`);
    viewExportCommands.notifyExportSucceeded(name);
  } catch (error) {
    handleSaveFailure({
      error,
      format: EXPORT_FORMAT.SVG,
      showExportFailureModal,
    });
  }

  profiling.endTransaction(transaction, {
    viewId: name,
    metadata: mutableExportMetaData,
  });
  viewExportCommands.notifyExportFinished(name);
  return true;
};

const runGraphComponentExport = async ({
  container,
  graphComponent,
  exportedViewMetadata,
  format,
  fileName,
  trackingFunction,
  getStyleSheets,
  showExportFailureModal,
  labels,
  canvasToSvg,
}: ExportHandlerArgs &
  WithGraphComponent &
  WithExportFormat &
  WithCanvasToSvg) => {
  const transaction = getExportProfilingTransaction();

  if (format === EXPORT_FORMAT.PNG) {
    const handledDirectly = await exportGraphComponentToPng({
      container,
      graphComponent,
      exportedViewMetadata,
      format,
      fileName,
      trackingFunction,
      getStyleSheets,
      showExportFailureModal,
      labels,
      transaction,
    });
    if (handledDirectly) return;
  }

  if (format === EXPORT_FORMAT.SVG && canvasToSvg) {
    const handledDirectly = await exportGraphComponentToSvg({
      container,
      graphComponent,
      exportedViewMetadata,
      format,
      fileName,
      trackingFunction,
      getStyleSheets,
      showExportFailureModal,
      labels,
      transaction,
    });
    if (handledDirectly) return;
  }

  const yFilesPrepareForExportSpan = profiling.startSpan(
    transaction,
    'yFiles - exportGraphComponent - prepareSVGExport'
  );
  const svgClone = prepareSVGExport({ graphComponent });

  profiling.endSpan(yFilesPrepareForExportSpan);

  const containerResolved = resolve(container);

  if (!containerResolved || !svgClone) {
    reportExportFailure(Error('Export found no graph component or container'));
    return;
  }

  svgClone.classList.add('yfiles-svg-export');
  return runSVGViewExport({
    transaction,
    container: containerResolved,
    svgClone,
    exportedViewMetadata,
    format,
    fileName,
    trackingFunction,
    getStyleSheets,
    showExportFailureModal,
    labels,
  });
};

export const exportGraphComponent = exportInFlight(runGraphComponentExport);

const cloneNodeForExport = <T extends Element>(node: T | null) => {
  const result = node?.cloneNode(true) as T;
  result?.classList?.add('forExport');

  return result;
};

const runSVGViewExport = async ({
  transaction,
  container,
  svgClone,
  exportedViewMetadata,
  format,
  fileName,
  trackingFunction,
  getStyleSheets,
  showExportFailureModal,
  labels,
}: ExportHandlerArgs &
  WithExportFormat &
  WithSVGClone &
  WithExportProfilingTransaction) => {
  const mutableExportMetaData = composeExportMetadata(
    exportedViewMetadata,
    format
  );

  const { name, isViewpointMode } = exportedViewMetadata;

  const resolvedContainer = resolve(container);
  const legendElement = findBesideContainer(
    resolvedContainer,
    VIEW_LEGEND_SELECTOR
  );
  const headerElement =
    format === EXPORT_FORMAT.PNG
      ? findBesideContainer(resolvedContainer, VIEW_EXPORT_HEADER_SELECTOR)
      : undefined;
  const resolvedSvgClone =
    svgClone ??
    (resolvedContainer
      ? cloneNodeForExport(resolvedContainer.querySelector('svg'))
      : null);
  if (!resolvedContainer || !resolvedSvgClone) {
    reportExportFailure(Error('Export found no SVG to export'));
    return;
  }
  viewExportCommands.notifyExportStarted(name);

  const exportFromClone = async (): Promise<void> => {
    // Create a clone container and append it to the SVG container
    // in order to get the same styles
    const cloneContainer = document.createElement('div');
    cloneContainer.style.position = 'absolute';
    cloneContainer.style.visibility = 'hidden';
    cloneContainer.prepend(resolvedSvgClone);
    resolvedContainer.prepend(cloneContainer);
    try {
      trackViewExport(trackingFunction, { name, format, isViewpointMode });

      const { offsetWidth, offsetHeight } = cloneContainer;
      let exportImage = true;
      if (offsetWidth > 15000 || offsetHeight > 15000) {
        exportImage = Boolean(await confirmLargeFile(labels));
      }
      if (exportImage) {
        try {
          const blob = await getExportBlob({
            mutableTrackingData: mutableExportMetaData,
            svg: resolvedSvgClone,
            format,
            legendElement,
            headerElement,
            getStyleSheets,
            name,
            profilingTransaction: transaction,
          });
          const fileSaverSpan = profiling.startSpan(
            transaction,
            'waiting for file saver'
          );
          deliverExport(blob, `${fileName || name}.${format}`);
          profiling.endSpan(fileSaverSpan);
          viewExportCommands.notifyExportSucceeded(name);
        } catch (error) {
          handleSaveFailure({ error, format, showExportFailureModal });
        }
      }

      profiling.endTransaction(transaction, {
        viewId: name,
        metadata: mutableExportMetaData,
      });
    } finally {
      cloneContainer.remove();
      // Need to trigger a resize for safari to render full height
      if (isSafari()) {
        window.dispatchEvent(new window.Event('resize'));
      }
      viewExportCommands.notifyExportFinished(name);
    }
  };

  return new Promise<void>((resolve, reject) => {
    setTimeout(() => {
      exportFromClone().then(resolve, reject);
    });
  });
};

const exportSVGView = exportInFlight(runSVGViewExport);

// containerElement is the element with the class name 'contentPane'
const runHtmlToPngExport = async ({
  container,
  exportedViewMetadata,
  transaction,
  trackingFunction = noop,
  getStyleSheets,
  labels,
}: ExportHandlerArgs<HTMLElement> & WithExportProfilingTransaction) => {
  const { name, isViewpointMode } = exportedViewMetadata;

  const mutableExportMetaData = composeExportMetadata(
    exportedViewMetadata,
    EXPORT_FORMAT.PNG
  );

  const resolvedContainer = resolve(container);
  if (!resolvedContainer) {
    reportExportFailure(Error('Export found no container'));
    return;
  }

  trackViewExport(trackingFunction, {
    name,
    format: TrackingExportFormat.PNG,
    isViewpointMode,
  });

  resolvedContainer.classList?.add('forExport');
  await document.fonts.ready;

  viewExportCommands.notifyExportStarted(name);

  try {
    const canvas = await htmlToCanvas(
      resolvedContainer,
      name,
      getStyleSheets,
      mutableExportMetaData,
      transaction,
      exportedViewMetadata.isDiscoverViewpoint,
      labels
    );
    if (canvas) {
      const blob = await canvasToBlob(canvas);
      const fileSaverSpan = profiling.startSpan(
        transaction,
        'waiting for file saver'
      );
      deliverExport(blob, `${name}.png`);
      profiling.endSpan(fileSaverSpan);
      viewExportCommands.notifyExportSucceeded(name);
    }
  } catch (error) {
    reportExportFailure(error);
    logError(error as Error, 'Failed to save export');
  }

  profiling.endTransaction(transaction, {
    viewId: name,
    metadata: mutableExportMetaData,
  });

  resolvedContainer.classList?.remove('forExport');
  resolvedContainer.querySelectorAll('.hideLegendDuringExport').forEach(el => {
    el.classList.remove('hideLegendDuringExport');
  });
  viewExportCommands.notifyExportFinished(name);
};

export const exportHtmlToPng = exportInFlight(runHtmlToPngExport);

export const getExportsForHtmlView = ({
  exportedViewMetadata,
  container,
  showPresentationOption = true,
  trackingFunction = noop,
  addToPresentation = noop,
  getStyleSheets,
  labels,
}: ExportHandlerArgs<HTMLElement>) => {
  const exportToPngConfig = {
    exportToPng: exportInFlight(async () => {
      const viewId = exportedViewMetadata.name;
      const viewInstanceId = exportedViewMetadata.viewInstanceId;

      try {
        if (PARTIALLY_RENDERED_VIEWS.has(viewId) && viewInstanceId) {
          // Wait for the view to be ready for export (partially rendered views will need to
          // render their entire contents)

          // Dispatch action to request view readiness for export
          dispatchAction(viewExportRequested(), viewInstanceId);

          // The view shall respond within 1 minute, if it does not, we say that
          // the view is too large for export and abort
          await firstValueFrom(
            action$.pipe(
              ofType(viewReadyForExport),
              withNamespace(viewInstanceId),
              take(1),
              timeout({
                each: 60000,
                with: () => {
                  if (!isExportCapturing()) {
                    // say that the view is too large for export
                    alert({
                      title: labels?.exportFailedTitle ?? 'Export Failed',
                      subtitle:
                        labels?.viewTooLargeSubtitle ??
                        'The view is too large for export. Please try reducing the size of the view.',
                      confirmButtonTitle: labels?.okButton ?? 'OK',
                    });
                  }

                  // abort the function by throwing an error
                  throw new Error(
                    `View ${viewId} is too large for export - timeout after 60 seconds`
                  );
                },
              })
            )
          );
        }

        // Now proceed with the actual export
        const transaction = getExportProfilingTransaction();
        await runHtmlToPngExport({
          transaction,
          exportedViewMetadata,
          container,
          trackingFunction,
          getStyleSheets,
          labels,
        });
      } catch (error) {
        reportExportFailure(error);
        logError(
          error as Error,
          `Failed to wait for view ${viewId} to be ready for export`
        );
        viewExportCommands.notifyExportFinished(viewId);
      }
    }),
    addToPresentation: undefined,
  };

  if (!showPresentationOption) return exportToPngConfig;

  return {
    ...exportToPngConfig,
    addToPresentation: () =>
      addToPresentation(
        exportedViewMetadata.name,
        exportedViewMetadata.isViewpointMode
      ),
  };
};

export const getExportsForSvgView = ({
  exportedViewMetadata,
  container,
  showPresentationOption = true,
  trackingFunction = noop,
  addToPresentation = noop,
  getStyleSheets,
  labels,
}: ExportHandlerArgs) => {
  const exportsForSVG = {
    exportToSvg: async () => {
      const transaction = getExportProfilingTransaction();
      await exportSVGView({
        transaction,
        container,
        exportedViewMetadata,
        format: EXPORT_FORMAT.SVG,
        trackingFunction,
        getStyleSheets,
        labels,
      });
    },
    exportToPng: async () => {
      const transaction = getExportProfilingTransaction();
      await exportSVGView({
        transaction,
        container,
        exportedViewMetadata,
        format: EXPORT_FORMAT.PNG,
        trackingFunction,
        getStyleSheets,
        labels,
      });
    },
    addToPresentation: undefined,
  };

  if (!showPresentationOption) return exportsForSVG;

  return {
    ...exportsForSVG,
    addToPresentation: () =>
      addToPresentation(
        exportedViewMetadata.name,
        exportedViewMetadata.isViewpointMode
      ),
  };
};

export const getExportsForYFilesView = ({
  exportedViewMetadata,
  container,
  graphComponent,
  showPresentationOption = true,
  pngConfirmExport = async () => true,
  trackingFunction = noop,
  addToPresentation = noop,
  getStyleSheets,
  showExportFailureModal = noop,
  exportToLucidchart = noop,
  labels,
  canvasToSvg,
}: ExportHandlerArgs & WithGraphComponent & WithCanvasToSvg) => {
  const exportsForYFiles = {
    exportToSvg: async () => {
      await exportGraphComponent({
        container,
        graphComponent,
        format: EXPORT_FORMAT.SVG,
        exportedViewMetadata,
        trackingFunction,
        getStyleSheets,
        showExportFailureModal,
        labels,
        canvasToSvg,
      });
    },
    exportToPng: async () => {
      const confirmed = await pngConfirmExport();
      if (!confirmed) return;

      await exportGraphComponent({
        container,
        graphComponent,
        format: EXPORT_FORMAT.PNG,
        exportedViewMetadata,
        trackingFunction,
        getStyleSheets,
        showExportFailureModal,
        labels,
      });
    },
    exportToLucidchart,
    addToPresentation: undefined,
  };

  if (!showPresentationOption) return exportsForYFiles;

  return {
    ...exportsForYFiles,
    addToPresentation: () =>
      addToPresentation(
        exportedViewMetadata.name,
        exportedViewMetadata.isViewpointMode
      ),
  };
};
